/**
 * Folk & Shepherd — canvas configurator controller.
 *
 * Two-axis sibling of assets/configurator-v2.js (Size x Frame only — no
 * Mount control, no "No Frame" option, every canvas variant has one of
 * the 4 floater colours). Deliberately a separate file rather than a
 * shared/parameterised class: this is a genuinely separate page per
 * Ryan's request, and duplicating the ~proven pattern here means a bug
 * fixed on one page can't silently reintroduce itself on the other by a
 * shared-code edit landing wrong for one of the two shapes. The pieces
 * that *are* shared — the visual engine (frame-engine.js) and its config
 * (frame-engine-config.json, "canvas" family) — stay genuinely shared.
 *
 * UI matches the print page one for one (swatch Frame dropdown with
 * add-on prices, tick row, size line, price, Add to cart) — the dropdown,
 * option-name matching and money formatting below are copied verbatim
 * from assets/configurator-v2.js. Canvas differs only in having canvas
 * frames as the sole frame option and never a Mount control.
 *
 * Same two price sources as the print configurator:
 *  - "placeholder": config.pricing.placeholder.canvas[size], flat per
 *    size — frame colour never changes price (there's only one family
 *    here, so that's the whole pricing model). Add to cart stays
 *    disabled, GBP only.
 *  - "variants": real variant price via option1 (Size) + option2
 *    (Frame) match, no option3 — canvas products only have two options.
 */
(function () {
  'use strict';

  function qs(root, sel) { return root.querySelector(sel); }
  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try { return JSON.parse(el.textContent); } catch (e) { return fallback; }
  }

  class CanvasConfigurator extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.priceSource = this.dataset.priceSource === 'variants' ? 'variants' : 'placeholder';
      this.orientation = this.dataset.orientation === 'portrait' ? 'portrait' : this.dataset.orientation === 'square' ? 'square' : 'landscape';
      // sections/main-product.liquid sets data-currency (cart.currency.iso_code).
      this.currencyIso = this.dataset.currency || this.dataset.currencyIso || 'GBP';
      this.locale = this.dataset.locale || undefined;

      this.stageEl = qs(this, '[data-canvas-cfg-stage]');
      this.frameAssets = parseJSON(qs(this, '[data-canvas-cfg-frame-assets]'), {});
      this.artworkUrl = (parseJSON(qs(this, '[data-canvas-cfg-artwork]'), {}) || {}).url || '';
      this.variants = parseJSON(qs(this, '[data-canvas-cfg-variants]'), []) || [];

      this.sizeInputs = Array.prototype.slice.call(this.querySelectorAll('[data-canvas-cfg-sizes] input[type="radio"]'));
      this.frameSelect = qs(this, '[data-canvas-cfg-frame]');
      this.finishedSizeEl = qs(this, '[data-canvas-cfg-finished-size]');
      this.priceEl = qs(this, '[data-canvas-cfg-price]');
      this.hiddenInput = qs(this, '[data-canvas-cfg-variant-input]');
      this.submitButton = qs(this, '[data-canvas-cfg-submit]');
      this.form = this.querySelector('form');
      this.imageView = qs(this, '[data-canvas-cfg-image-view]');
      this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-canvas-cfg-show-mockup], [data-canvas-cfg-show-image]'));
      this.mockupThumb = qs(this, '[data-canvas-cfg-show-mockup]');

      if (!this.stageEl || !this.frameSelect || !window.FolkFrameEngine) {
        return; // malformed markup or engine failed to load — fail quiet, no-JS fallback still works
      }

      this.dropdowns = {
        frame: this.enhanceDropdown(qs(this, '[data-canvas-cfg-dropdown="frame"]'), this.frameSelect)
      };

      this.assetUrl = function (assetsKey, piece) {
        return this.frameAssets[assetsKey + '-' + piece] || '';
      }.bind(this);

      var checkedSize = this.sizeInputs.filter(function (i) { return i.checked; })[0];
      this.state = {
        size: checkedSize ? checkedSize.value : (this.sizeInputs[0] && this.sizeInputs[0].value),
        frame: this.frameSelect.value
      };

      fetch(this.dataset.configUrl)
        .then(function (r) { return r.json(); })
        .then(function (config) {
          this.config = config;
          this.applyUrlVariant();
          this.bindEvents();
          this.render();
          this.preloadFrames();
          this.observeResize();
        }.bind(this))
        .catch(function (err) {
          console.error('canvas-configurator: failed to load config', err);
        });
    }

    applyUrlVariant() {
      if (this.priceSource !== 'variants') return;
      var params = new URLSearchParams(window.location.search);
      var variantId = params.get('variant');
      if (!variantId) return;
      var variant = this.variants.filter(function (v) { return String(v.id) === String(variantId); })[0];
      if (!variant) return;

      this.state = { size: variant.option1, frame: variant.option2 };
      var sizeInput = this.sizeInputs.filter(function (i) { return i.value === variant.option1; })[0];
      if (sizeInput) sizeInput.checked = true;
      if (this.frameOptionExists(variant.option2)) this.frameSelect.value = variant.option2;
    }

    // ---- option value -> engine config key ------------------------------
    //
    // The product's option values ("Box frame – Oak", "White mount",
    // "16 × 24 in") are written for shoppers; assets/frame-engine-config.json
    // keys ("Box Frame - Oak", "White Mount", '24×16"') are the engine's.
    // Match them loosely (case, dash style, spacing) rather than keeping two
    // copies of every name in sync.

    normKey(value) {
      return String(value || '').toLowerCase().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, ' ').trim();
    }

    configKey(map, value) {
      var target = this.normKey(value);
      return Object.keys(map || {}).filter(function (k) { return this.normKey(k) === target; }, this)[0] || null;
    }

    // ("12 × 18 in" portrait is the config's 18×12" entry).
    sizeKey(value) {
      var nums = (String(value).match(/\d+(?:\.\d+)?/g) || []).slice(0, 2).map(Number);
      if (nums.length < 2) return null;
      var long = Math.max(nums[0], nums[1]), short = Math.min(nums[0], nums[1]);
      var sizes = this.config.sizes;
      return Object.keys(sizes).filter(function (k) {
        return Math.max(sizes[k].w_in, sizes[k].h_in) === long && Math.min(sizes[k].w_in, sizes[k].h_in) === short;
      })[0] || null;
    }

    frameOptionExists(value) {
      return Array.prototype.some.call(this.frameSelect.options, function (o) { return o.value === value; });
    }

    bindEvents() {
      this.sizeInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          this.state.size = input.value;
          this.render();
        }.bind(this));
      }, this);

      this.frameSelect.addEventListener('change', function () {
        this.state.frame = this.frameSelect.value;
        this.render();
      }.bind(this));

      this.thumbs.forEach(function (btn) {
        btn.addEventListener('click', function () {
          if (btn.hasAttribute('data-canvas-cfg-show-mockup')) {
            this.showMockup();
          } else {
            this.showImage(btn.getAttribute('data-canvas-cfg-show-image'));
          }
          this.setActiveThumb(btn);
        }.bind(this));
      }, this);
    }

    // ---- stage / other-images view -----------------------------------
    // Identical pattern to assets/configurator-v2.js, including the fix
    // for the same bug: hiding the stage collapses its clientWidth to 0,
    // which the ResizeObserver reports as a resize — render then falls
    // back to a hardcoded size, baking in a wrongly-sized mockup while
    // invisible. renderStageOnly() skips while hidden/sizeless;
    // showMockup() always re-renders on the way back in.

    showMockup() {
      if (this.stageEl) this.stageEl.hidden = false;
      if (this.imageView) this.imageView.hidden = true;
      return this.config ? this.renderStage() : null;
    }

    showImage(url) {
      if (!this.imageView || !url) return;
      this.imageView.src = url;
      this.imageView.hidden = false;
      if (this.stageEl) this.stageEl.hidden = true;
    }

    setActiveThumb(activeBtn) {
      this.thumbs.forEach(function (btn) {
        var active = btn === activeBtn;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', String(active));
      });
    }

    observeResize() {
      if (typeof ResizeObserver === 'undefined') return;
      var raf = null;
      var ro = new ResizeObserver(function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () { this.renderStageOnly(); }.bind(this));
      }.bind(this));
      ro.observe(this.stageEl);
    }

    // ---- pricing ----------------------------------------------------

    // Full price of a Size × Frame combination, in minor units, or null
    // when that variant doesn't exist.
    price(sizeValue, frameValue) {
      if (this.priceSource === 'placeholder') {
        return (this.config.pricing.placeholder.canvas || {})[this.sizeKey(sizeValue)] || 0;
      }
      var variant = this.findVariant(sizeValue, frameValue);
      return variant ? variant.price : null;
    }

    findVariant(sizeValue, frameValue) {
      return this.variants.filter(function (v) {
        return v.option1 === sizeValue && v.option2 === frameValue;
      })[0] || null;
    }

    formatMoney(minorUnits, opts) {
      opts = opts || {};
      if (minorUnits == null) return '—';
      var currency = this.priceSource === 'placeholder' ? (this.config.pricing.placeholder.currency || 'GBP') : this.currencyIso;
      var locale = this.priceSource === 'placeholder' ? 'en-GB' : this.locale;
      var amount = minorUnits / 100;
      var formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: (amount % 1 === 0) ? 0 : 2,
        maximumFractionDigits: 2
      });
      var formatted = formatter.format(Math.abs(amount));
      if (opts.signed && minorUnits !== 0) {
        return (minorUnits > 0 ? '+ ' : '− ') + formatted;
      }
      return formatted;
    }

    // ---- rendering ----------------------------------------------------

    stageOpts() {
      var stagePx = Math.round(this.stageEl.clientWidth) || 800;
      return {
        config: this.config,
        frameValue: this.configKey(this.config.frames, this.state.frame) || undefined,
        mountValue: undefined, // canvas never has a mount
        sizeValue: this.sizeKey(this.state.size),
        orientation: this.orientation,
        artworkUrl: this.artworkUrl,
        assetUrl: this.assetUrl,
        stagePx: stagePx
      };
    }

    // Draws the mockup once this choice's frame images are loaded (the
    // previous mockup stays up until then — no half-drawn frame), but
    // returns the geometry straight away so the size line updates at once.
    renderStage() {
      var opts = this.stageOpts();
      window.FolkFrameEngine.renderWhenReady(this.stageEl, opts);
      return window.FolkFrameEngine.computeGeometry(opts.config, opts.frameValue, opts.mountValue,
        opts.sizeValue, opts.orientation, opts.stagePx, opts.printSpecs);
    }

    // Every frame colour's side images, fetched quietly once the page has
    // settled, so switching frames is instant.
    preloadFrames() {
      var urls = Object.keys(this.frameAssets).map(function (k) { return this.frameAssets[k]; }, this);
      var go = function () { window.FolkFrameEngine.preload(urls.concat(this.artworkUrl)); }.bind(this);
      if ('requestIdleCallback' in window) window.requestIdleCallback(go, { timeout: 3000 });
      else setTimeout(go, 1500);
    }

    renderStageOnly() {
      if (!this.config) return;
      if (!this.stageEl || this.stageEl.hidden || !this.stageEl.clientWidth) return;
      this.renderStage();
    }

    render() {
      if (!this.config) return;
      var geo = this.showMockup();
      if (this.mockupThumb) this.setActiveThumb(this.mockupThumb);
      this.updateFinishedSize(geo);
      this.updateFrameLabels();
      this.updatePrice();
      this.updateVariant();
    }

    updateFinishedSize(geo) {
      if (!this.finishedSizeEl) return;
      // Same wording and format as the print page's framed size line.
      function num(v) { return String(Number(v.toFixed(1))); } // 12.0 -> "12", 62.4 -> "62.4"
      function dims(w, h, unit) { return num(w) + ' × ' + num(h) + ' ' + unit; }
      var mm = geo.mm;
      this.finishedSizeEl.textContent = 'Framed size approx ' + dims(mm.outerW / 25.4, mm.outerH / 25.4, 'in') +
        ' (' + dims(mm.outerW / 10, mm.outerH / 10, 'cm') + ')';
    }

    // Canvas has no "Unframed", so a frame's add-on is measured from the
    // cheapest frame at this size — normally every colour costs the same,
    // and no add-on shows, exactly like a zero add-on on the print page.
    updateFrameLabels() {
      var prices = {};
      Array.prototype.forEach.call(this.frameSelect.options, function (opt) {
        prices[opt.value] = this.price(this.state.size, opt.value);
      }, this);
      var known = Object.keys(prices).map(function (k) { return prices[k]; }).filter(function (p) { return p != null; });
      var baseline = known.length ? Math.min.apply(null, known) : null;
      var addons = {};
      Array.prototype.forEach.call(this.frameSelect.options, function (opt) {
        addons[opt.value] = (prices[opt.value] == null || baseline == null) ? null : prices[opt.value] - baseline;
        opt.textContent = this.optionText(opt.value, addons[opt.value]);
      }, this);
      this.syncDropdown(this.dropdowns.frame, this.state.frame, addons);
    }

    hasAddon(amount) {
      return amount != null && isFinite(amount) && amount !== 0;
    }

    addonText(amount) {
      return this.hasAddon(amount) ? this.formatMoney(amount, { signed: true }) : '';
    }

    optionText(value, amount) {
      return this.hasAddon(amount) ? value + '  ' + this.addonText(amount) : value;
    }

    // ---- swatch dropdowns ----------------------------------------------
    //
    // The design's Frame/Mount pickers show a swatch per option, which a
    // native <select> can't. The select stays the source of truth (and the
    // no-JS fallback): picking an option here sets select.value and fires
    // the same 'change' event the rest of this file already listens to.

    enhanceDropdown(root, select) {
      if (!root || !select) return null;
      var dd = {
        root: root,
        select: select,
        trigger: qs(root, '.cv2-dropdown__trigger'),
        current: qs(root, '[data-cv2-current]'),
        list: qs(root, '[role="listbox"]'),
        options: Array.prototype.slice.call(root.querySelectorAll('[role="option"]'))
      };
      var native = select.closest('[data-canvas-cfg-native]');
      if (native) native.hidden = true;
      root.hidden = false;
      this.syncDropdown(dd, select.value, {});

      dd.options.forEach(function (opt, i) {
        opt.id = select.id + '-opt-' + i;
        opt.addEventListener('click', function () { this.chooseOption(dd, opt); }.bind(this));
      }, this);

      dd.trigger.addEventListener('click', function () {
        if (dd.list.hidden) this.openDropdown(dd); else this.closeDropdown(dd);
      }.bind(this));

      dd.trigger.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          this.openDropdown(dd);
        }
      }.bind(this));

      dd.list.addEventListener('keydown', function (e) {
        var idx = dd.options.indexOf(dd.active);
        if (e.key === 'ArrowDown') { e.preventDefault(); this.setActiveOption(dd, dd.options[Math.min(idx + 1, dd.options.length - 1)]); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); this.setActiveOption(dd, dd.options[Math.max(idx - 1, 0)]); }
        else if (e.key === 'Home') { e.preventDefault(); this.setActiveOption(dd, dd.options[0]); }
        else if (e.key === 'End') { e.preventDefault(); this.setActiveOption(dd, dd.options[dd.options.length - 1]); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (dd.active) this.chooseOption(dd, dd.active); }
        else if (e.key === 'Escape') { e.preventDefault(); this.closeDropdown(dd, true); }
        else if (e.key === 'Tab') { this.closeDropdown(dd); }
      }.bind(this));

      document.addEventListener('click', function (e) {
        if (!dd.list.hidden && !root.contains(e.target)) this.closeDropdown(dd);
      }.bind(this));

      return dd;
    }

    openDropdown(dd) {
      if (dd.trigger.disabled) return;
      ['frame', 'mount'].forEach(function (k) {
        if (this.dropdowns[k] && this.dropdowns[k] !== dd) this.closeDropdown(this.dropdowns[k]);
      }, this);
      dd.list.hidden = false;
      dd.root.classList.add('is-open');
      dd.trigger.setAttribute('aria-expanded', 'true');
      var selected = dd.options.filter(function (o) { return o.dataset.value === dd.select.value; })[0];
      this.setActiveOption(dd, selected || dd.options[0]);
      dd.list.focus();
    }

    closeDropdown(dd, refocus) {
      if (!dd || dd.list.hidden) return;
      dd.list.hidden = true;
      dd.root.classList.remove('is-open');
      dd.trigger.setAttribute('aria-expanded', 'false');
      dd.list.removeAttribute('aria-activedescendant');
      if (refocus) dd.trigger.focus();
    }

    setActiveOption(dd, opt) {
      if (!opt) return;
      dd.options.forEach(function (o) { o.classList.toggle('is-active', o === opt); });
      dd.active = opt;
      dd.list.setAttribute('aria-activedescendant', opt.id);
      opt.scrollIntoView({ block: 'nearest' });
    }

    chooseOption(dd, opt) {
      if (opt.getAttribute('aria-disabled') === 'true') return;
      this.closeDropdown(dd, true);
      if (dd.select.value === opt.dataset.value) return;
      dd.select.value = opt.dataset.value;
      dd.select.dispatchEvent(new Event('change'));
    }

    // Price chips in the list ("+ £56") and the closed trigger
    // ("Box Frame | Oak - £56"), matching the Figma states.
    syncDropdown(dd, selectedValue, addons) {
      if (!dd) return;
      dd.options.forEach(function (opt) {
        var priceEl = qs(opt, '[data-cv2-price]');
        if (priceEl) priceEl.textContent = this.addonText(addons[opt.dataset.value]);
        opt.setAttribute('aria-selected', String(opt.dataset.value === selectedValue));
      }, this);

      var selected = dd.options.filter(function (o) { return o.dataset.value === selectedValue; })[0] || dd.options[0];
      if (!selected) return;
      var swatch = qs(selected, '.cv2-swatch');
      var label = qs(selected, '.cv2-dropdown__label');
      dd.current.innerHTML = '';
      if (swatch) dd.current.appendChild(swatch.cloneNode(true));
      var text = document.createElement('span');
      text.className = 'cv2-dropdown__label';
      text.textContent = label ? label.textContent : selectedValue;
      dd.current.appendChild(text);
      var price = document.createElement('span');
      price.className = 'cv2-dropdown__price';
      price.textContent = this.addonText(addons[selected.dataset.value]);
      dd.current.appendChild(price);
    }

    updatePrice() {
      if (!this.priceEl) return;
      this.priceEl.textContent = this.formatMoney(this.price(this.state.size, this.state.frame));
    }

    updateVariant() {
      if (this.priceSource !== 'variants') return; // placeholder mode: Add to cart stays disabled, nothing to resolve

      var variant = this.findVariant(this.state.size, this.state.frame);
      if (variant) {
        if (this.hiddenInput) {
          this.hiddenInput.value = variant.id;
          this.hiddenInput.disabled = false;
        }
        if (this.submitButton) {
          this.submitButton.disabled = !variant.available;
          this.submitButton.toggleAttribute('aria-disabled', !variant.available);
        }
        this.syncUrl(variant.id);
      } else {
        if (this.hiddenInput) this.hiddenInput.disabled = true;
        if (this.submitButton) {
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
        }
      }
    }

    syncUrl(variantId) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  if (!customElements.get('canvas-configurator')) {
    customElements.define('canvas-configurator', CanvasConfigurator);
  }
})();
